import type {PropsWithChildren} from 'react'
import {createContext, use, useMemo} from 'react'

import type {ThreadSummary} from '../../commit-conversations/types'
import type {HunkData} from '../helpers/hunk-data-helpers'
import type {ClientDiffLine} from '../types'
import {DiffLineSelectionContextProvider} from './DiffLineSelectionContext'

/**
 * Per-row diff state. File-level metadata (filePath, fileAnchor, isSplit,
 * fileLineCount, diffEntryId, diffContext, setInGridMode) lives in
 * `DiffFileContext`; per-row selection lives in `DiffLineSelectionContext`
 * (see `useDiffLineSelected`). Both are split out so this context's value
 * identity stays stable across the file's lifetime / across selection clicks.
 */
export type DiffLineContextData = {
  diffLine: ClientDiffLine
  rowId: string
  isLeftSide?: boolean
  hasHiddenUnicodeCharacters?: boolean
  currentHunk?: HunkData
  nextHunk?: HunkData
  previousHunk?: HunkData
  modifiedLineThreads?: ThreadSummary[]
  modifiedLineHasThreads?: boolean
  originalLineThreads?: ThreadSummary[]
  originalLineHasThreads?: boolean
}

/**
 * Provider props mirror the data shape but also accept `isRowSelected`, which
 * is routed into `DiffLineSelectionContext` so the line-data value stays
 * reference-equal across selection toggles.
 */
type DiffLineContextProviderProps = DiffLineContextData & {
  isRowSelected?: boolean
}

const DiffLineContext = createContext<DiffLineContextData | null>(null)

export function DiffLineContextProvider({
  children,
  diffLine,
  rowId,
  isLeftSide,
  isRowSelected = false,
  hasHiddenUnicodeCharacters,
  currentHunk,
  nextHunk,
  previousHunk,
  modifiedLineThreads,
  originalLineThreads,
}: PropsWithChildren<DiffLineContextProviderProps>) {
  const contextData = useMemo(
    () => ({
      diffLine,
      rowId,
      isLeftSide,
      hasHiddenUnicodeCharacters,
      currentHunk,
      nextHunk,
      previousHunk,
      modifiedLineThreads,
      modifiedLineHasThreads: modifiedLineThreads && modifiedLineThreads.length > 0,
      originalLineThreads,
      originalLineHasThreads: originalLineThreads && originalLineThreads.length > 0,
    }),
    [
      diffLine,
      rowId,
      isLeftSide,
      hasHiddenUnicodeCharacters,
      currentHunk,
      nextHunk,
      previousHunk,
      modifiedLineThreads,
      originalLineThreads,
    ],
  )

  return (
    <DiffLineSelectionContextProvider isRowSelected={isRowSelected}>
      <DiffLineContext value={contextData}>{children}</DiffLineContext>
    </DiffLineSelectionContextProvider>
  )
}

export function useDiffLineContext(): DiffLineContextData {
  const contextData = use(DiffLineContext)
  if (!contextData) {
    throw new Error('useDiffLineContext must be used within a DiffLineContextProvider')
  }

  return contextData
}
